// src/utils/transactionLines.js
//
// THE single definition of "turn cart lines into transaction_items rows".
//
// It used to have three: the page-local copy in pages/pos/CashierSalesPage.js,
// the extracted copy in pos-app/surfaces/cashier/lib/transactionFlow.js, and
// the flat one inlined in utils/OrderManagement.createTransactionFromOrder.
// Only the middle one ever learned about modifier hierarchy, so the other two
// posted nested modifiers parented to the PRODUCT — which makes the server's
// selection validator read the child's group as unanswered and reject the sale
// with 400. Three copies is what caused that bug; this module exists so there
// is one.
//
// It lives in utils/ and imports NOTHING on purpose. transactionFlow.js
// re-exports it (so the cashier charge path keeps its single import point) and
// OrderManagement.js imports it directly. Putting it in either of those two
// files instead would make them import each other — transactionFlow.js already
// re-exports createTransactionFromOrder *from* OrderManagement.js.
//
// See docs/superpowers/specs/2026-08-24-nested-addons-design.md §4.

let lineCounter = 0;
const nextLineId = () => {
    lineCounter += 1;
    return `L${Date.now().toString(36)}${lineCounter.toString(36)}`;
};

// ---------------------------------------------------------------------------
// createItemsWithAddons
//
// Emits one row per line, parent before child, each carrying line_id /
// parent_line_id / group_id. The old version consolidated identical rows and
// linked modifiers to their parent with a NOTES STRING, which meant two of the
// same product with different modifiers merged into one row and the hierarchy
// was lost at save time. Consolidation is deliberately gone: two lines that
// differ only by their modifiers are genuinely different lines.
//
// Legacy flat add-ons — taken before modifier groups existed, so carrying no
// group_id and no parent_code — still work: they root at the product line with
// a null group_id, which is exactly the shape the validator ignores.
// ---------------------------------------------------------------------------
export const createItemsWithAddons = (products) => {
    const items = [];

    products.forEach((product) => {
        const quantity = parseInt(product.quantity) || 1;
        const productLineId = nextLineId();
        const category = product.category || 'Unknown';

        items.push({
            line_id: productLineId,
            parent_line_id: null,
            group_id: null,
            product_code: product.product_code,
            product_name: product.name || 'Unknown Product',
            category,
            quantity,
            unit_price: parseFloat(product.selling_price) || 0,
            total_amount: (parseFloat(product.selling_price) || 0) * quantity,
            notes: product.notes || '',
        });

        // add_ons is flat and ordered parent-before-child, so a map from an
        // option's product_code to the line_id we just gave it is enough to
        // resolve every parent pointer in one pass.
        // Array.isArray, not `|| []`: a corrupt persisted order can carry a
        // non-array add_ons, and .forEach on it would throw mid-charge.
        const addOns = Array.isArray(product.add_ons) ? product.add_ons : [];
        const lineIdByCode = {};
        addOns.forEach((addon) => {
            // product_code and product_name are both NOT NULL server-side, so
            // an add-on missing either can never become a valid row — emitting
            // it would 422 the whole sale. The flat pending-order builder this
            // replaced dropped these too; keep dropping them.
            if (!addon || !addon.product_code || !addon.name) return;

            const lineId = nextLineId();
            lineIdByCode[addon.product_code] = lineId;
            const price = parseFloat(addon.price) || 0;
            items.push({
                line_id: lineId,
                parent_line_id: addon.parent_code
                    ? (lineIdByCode[addon.parent_code] || productLineId)
                    : productLineId,
                group_id: addon.group_id || null,
                product_code: addon.product_code,
                product_name: addon.name,
                category,
                quantity,
                unit_price: price,
                total_amount: price * quantity,
                notes: '',
            });
        });
    });

    return items;
};
